import { api } from './api';

export type CalendarSyncProviderId = 'google_calendar' | 'microsoft_365';

export interface CalendarSyncProviderStatus {
  provider: CalendarSyncProviderId;
  connected: boolean;
  emailAddress: string | null;
  syncEnabled: boolean;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  /** A connection exists but its sign-in expired or was revoked — show "Reconnect needed". */
  needsReconnect: boolean;
  /** The mailbox behind this provider, used as the reconnect target. */
  mailboxId: string | null;
}

export interface CalendarSyncStatus {
  providers: CalendarSyncProviderStatus[];
}

export async function fetchCalendarSyncStatus(): Promise<CalendarSyncStatus> {
  const res = await api.get<{ data: CalendarSyncStatus }>('/calendar-sync/status');
  return res.data.data;
}

export async function enableCalendarSync(provider: CalendarSyncProviderId): Promise<CalendarSyncStatus> {
  const res = await api.post<{ data: CalendarSyncStatus }>('/calendar-sync/enable', { provider });
  return res.data.data;
}

export async function disableCalendarSync(provider: CalendarSyncProviderId): Promise<CalendarSyncStatus> {
  const res = await api.post<{ data: CalendarSyncStatus }>('/calendar-sync/disable', { provider });
  return res.data.data;
}

export async function resyncCalendar(provider: CalendarSyncProviderId): Promise<CalendarSyncStatus> {
  const res = await api.post<{ data: CalendarSyncStatus }>('/calendar-sync/resync', { provider });
  return res.data.data;
}

// ─── Aggregate health (admin / support) ──────────────────────

export type ConnectionHealth = 'healthy' | 'stale' | 'error' | 'never';

export interface CalendarConnectionHealth {
  userId: string;
  emailAddress: string;
  health: ConnectionHealth;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  minutesSinceSync: number | null;
}

export interface CalendarSyncHealth {
  summary: {
    activeConnections: number;
    healthy: number;
    stale: number;
    error: number;
    never: number;
    failedJobs: number | null;
    lastPollAt: string | null;
    pollWorkerHealthy: boolean;
  };
  connections: CalendarConnectionHealth[];
}

export async function fetchCalendarSyncHealth(): Promise<CalendarSyncHealth> {
  const res = await api.get<{ data: CalendarSyncHealth }>('/calendar-sync/health');
  return res.data.data;
}
